import { Injectable } from '@angular/core';

export interface ServerTimingMetric {
  name: string;
  duration: number;
  description?: string;
}

export interface PerformanceMetrics {
  url: string;
  totalDuration: number;
  serverTimings: ServerTimingMetric[];
  timestamp: Date;
  userAgent: string;
  browser: string;
  os: string;
  isMobile: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PerformanceMonitoringService {
  private readonly STORAGE_KEY = 'performance_metrics';
  private readonly MAX_METRICS = 500; // Keep last 500 requests

  /**
   * Capture performance metrics for a given URL
   */
  captureMetrics(url: string): void {
    setTimeout(() => {
      try {
        const resourceEntries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        const urlPath = url.includes('://') ? new URL(url).pathname : url.split('?')[0];

        const apiCall = resourceEntries
          .filter(e => {
            const entryPath = e.name.includes('://') ? new URL(e.name).pathname : e.name;
            return entryPath.includes(urlPath) || e.name.includes(urlPath);
          })
          .sort((a, b) => b.startTime - a.startTime)[0];

        if (!apiCall) {
          console.warn(`[PerformanceMonitoring] No performance entry found for: ${url}`);
          return;
        }

        const serverTimings = this.parseServerTiming(apiCall);
        const browserInfo = this.getBrowserInfo();

        const metrics: PerformanceMetrics = {
          url: url,
          totalDuration: apiCall.duration,
          serverTimings: serverTimings,
          timestamp: new Date(),
          userAgent: navigator.userAgent,
          browser: browserInfo.browser,
          os: browserInfo.os,
          isMobile: browserInfo.isMobile
        };

        this.storeMetrics(metrics);
      } catch (error) {
        console.error('[PerformanceMonitoring] Error capturing metrics:', error);
      }
    }, 100);
  }

  private parseServerTiming(entry: PerformanceResourceTiming): ServerTimingMetric[] {
    const serverTimings: ServerTimingMetric[] = [];
    if (entry.serverTiming && entry.serverTiming.length > 0) {
      entry.serverTiming.forEach(timing => {
        serverTimings.push({
          name: timing.name,
          duration: timing.duration,
          description: timing.description || undefined
        });
      });
    } else {
      console.warn('[PerformanceMonitoring] No serverTiming data found on entry');
    }

    return serverTimings;
  }

  private storeMetrics(metrics: PerformanceMetrics): void {
    const existing = this.getMetrics();
    existing.push(metrics);

    // Keep only the last MAX_METRICS entries
    if (existing.length > this.MAX_METRICS) {
      existing.splice(0, existing.length - this.MAX_METRICS);
    }

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(existing));
  }

  /**
   * Get all stored metrics from localStorage
   */
  getMetrics(): PerformanceMetrics[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      // Convert timestamp strings back to Date objects
      return parsed.map((m: any) => ({
        ...m,
        timestamp: new Date(m.timestamp)
      }));
    } catch (error) {
      console.error('[PerformanceMonitoring] Error reading metrics from localStorage:', error);
      return [];
    }
  }

  /**
   * Get metrics for a specific URL pattern
   */
  getMetricsForUrl(urlPattern: string): PerformanceMetrics[] {
    return this.getMetrics().filter(m => m.url.includes(urlPattern));
  }

  /**
   * Get average duration for a specific URL pattern
   */
  getAverageDuration(urlPattern: string): number {
    const filtered = this.getMetricsForUrl(urlPattern);
    if (filtered.length === 0) return 0;

    const total = filtered.reduce((sum, m) => sum + m.totalDuration, 0);
    return total / filtered.length;
  }

  /**
   * Get average server timing for a specific metric name and URL pattern
   */
  getAverageServerTiming(urlPattern: string, metricName: string): number {
    const filtered = this.getMetricsForUrl(urlPattern);
    if (filtered.length === 0) return 0;

    const timings = filtered
      .map(m => m.serverTimings.find(t => t.name === metricName)?.duration || 0)
      .filter(d => d > 0);

    if (timings.length === 0) return 0;

    const total = timings.reduce((sum, d) => sum + d, 0);
    return total / timings.length;
  }

  /**
   * Get browser and device information
   */
  private getBrowserInfo(): { browser: string; os: string; isMobile: boolean } {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let os = 'Unknown';
    const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(ua);

    // Detect browser
    if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';

    // Detect OS
    if (ua.includes('Win')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

    return { browser, os, isMobile };
  }

  /**
   * Clear all stored metrics from localStorage
   */
  clearMetrics(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }
}
